/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for building available bulk operations
 */

import { useMemo } from "react";
import type { BulkOperationDefinition } from "./types";

interface UseBulkOperationsAvailableOptions<T> {
  operations: BulkOperationDefinition<T>[];
  keyExtractor: (item: T) => string;
  onBulkDelete?: (ids: string[]) => Promise<void>;
  onBulkExport?: (items: T[]) => Promise<void>;
}

/**
 * Hook for building available bulk operations list
 */
export function useBulkOperationsAvailable<T>({
  operations,
  keyExtractor,
  onBulkDelete,
  onBulkExport,
}: UseBulkOperationsAvailableOptions<T>) {
  // Add legacy operations to available operations
  const availableOperations: BulkOperationDefinition<T>[] = useMemo(() => {
    // Check if operations already include bulk-delete and bulk-export
    const hasDelete = operations.some((op) => op.id === "bulk-delete");
    const hasExport = operations.some((op) => op.id === "bulk-export");

    return [
      ...operations,
      // Only add legacy bulk-delete if not already in operations array
      ...(onBulkDelete && !hasDelete
        ? [
            {
              id: "bulk-delete",
              label: "Delete Selected",
              variant: "destructive" as const,
              requiresConfirmation: true,
              canUndo: false,
              handler: async (items: T[]) => {
                const ids = items.map(keyExtractor);
                await onBulkDelete(ids);
              },
            },
          ]
        : []),
      // Only add legacy bulk-export if not already in operations array
      ...(onBulkExport && !hasExport
        ? [
            {
              id: "bulk-export",
              label: "Export Selected",
              variant: "default" as const,
              requiresConfirmation: false,
              canUndo: false,
              handler: async (items: T[]) => {
                await onBulkExport(items);
              },
            },
          ]
        : []),
    ];
  }, [keyExtractor, onBulkDelete, onBulkExport, operations]);

  return { availableOperations };
}
